<template>
  <div>
    <div class="block block--with-light-background block--first">
      <div class="block__content block__content--wide">
        <Hero
          :data="pageData"
          @scroll-to-element="id => scrollToElementById(id)"
        />
      </div>
    </div>

    <div
      class="block block--with-image-background block--last"
      :style="backgroundImage ? `--bg-image: url(${backgroundImage}&w=440&h=auto); --bg-image-2x: url(${backgroundImage}&w=880&h=auto)` : ''"
    >
      <div class="block__content">
        <Form class="new-form-enabled">
          <div
            class="profession p-16 p-md-24 px-lg-32 py-lg-40"
            id="profession-items"
          >
            <Heading
              class="profession__heading text-center mb-4"
              kind="h2"
              type="h1"
            >
              {{ pageData.formTitle }}
            </Heading>

            <Heading
              class="heading--semibold heading--secondary-color text-center mt-0 mb-32"
              kind="h4"
            >
              {{ pageData.formSubtitle }}
            </Heading>

            <FormSection
              :description="$t('pages.profession.generator.typeOfDocument.description')"
              :image="require('~/images/us_form/notes.webp')"
              :title="sectionHeader"
              class="profession__type-wrapper"
              has-big-image
              has-two-columns-header
            >
              <template
                slot="control"
                v-if="!showTypeOfDocument"
              >
                <Anchor
                  class="profession__type-link"
                  @click="showTypeOfDocument = true"
                >
                  <span class="text--lg text--semibold">
                    {{ $t('pages.profession.generator.typeOfDocument.link') }}
                  </span>
                </Anchor>
              </template>

              <transition name="type-of-document-slide">
                <TypeOfDocument
                  v-if="showTypeOfDocument"
                  :config="fieldsConfig.typeOfDocument"
                  :document-types="resolvedDocumentTypes"
                  :has-three-columns-layout="!isUsaRegion"
                  hide-header
                  v-model="typeOfDocumentModel"
                />
              </transition>
            </FormSection>

            <hr class="my-32 delimiter">

            <div class="profession__search-wrapper">
              <FormGroup
                :input-value="search"
                :prop="'search'"
                class="search"
                floating-label
                :label="searchInputLabel"
              >
                <TextInput
                  @input="handleSearch"
                  autocomplete="new-password"
                  class="search__input"
                  clearable
                  data-test="search_query"
                  name="search"
                  size="large"
                  type="text"
                  v-model="search"
                />
              </FormGroup>

              <VatCalculation
                class="flex-shrink-0"
                v-if="!vatRateSelectHidden"
              />
            </div>

            <hr class="my-32 delimiter hidden-md-up">

            <EmptyContainer
              v-if="hasNoSearchResults"
              image-name="magnifier"
              without-spacing
            >
              <template slot="additional-content">
                <p
                  v-html="$t('pages.profession.generator.noResultsText', { query: debouncedSearch })"
                  class="text--lg text-center"
                />
              </template>
            </EmptyContainer>

            <div id="first-profession-item" />

            <div
              v-for="categoryEntry in filteredProfessionData"
              :key="`category-${categoryEntry.originalIndex}`"
              class="profession__category"
            >
              <Heading
                kind="h4"
                class="heading--semibold heading--highlighted mb-6"
              >
                {{ categoryEntry.originalIndex + 1 }}. {{ categoryEntry.category.category }}
              </Heading>

              <p class="text--secondary-color text--md mb-24">
                {{ categoryEntry.category.description }}
              </p>

              <div
                v-for="subCategoryEntry in categoryEntry.category.subcategories"
                :key="`subcategory-${categoryEntry.originalIndex}-${subCategoryEntry.originalIndex}`"
                class="profession__subcategory"
              >
                <div class="text--lg text--semibold">
                  {{ subCategoryEntry.subcategory.subcategory }}
                </div>

                <p class="text--secondary-color text--md mb-16 mb-md-24">
                  {{ subCategoryEntry.subcategory.description }}
                </p>

                <ProfessionItems
                  :key="`prof-items-${categoryEntry.originalIndex}-${subCategoryEntry.originalIndex}`"
                  :category-index="categoryEntry.originalIndex"
                  :config="fieldsConfig.invoiceItems"
                  :items="professionData[categoryEntry.originalIndex].subcategories[subCategoryEntry.originalIndex].items"
                  :visible-indexes="visibleIndexesFor(professionData[categoryEntry.originalIndex].subcategories[subCategoryEntry.originalIndex].items)"
                  :search-term="debouncedSearch"
                  :subcategory-index="subCategoryEntry.originalIndex"
                  :vat-rate-options="vatRateOptions"
                  :default-vat-rate="defaultVatRate"
                  :vat-rate-select-hidden="vatRateSelectHidden"
                  @updateItems="handleProfessionItemsUpdate"
                />
              </div>
            </div>
          </div>

          <div
            class="invoice p-16 p-md-24 px-lg-32 py-lg-40"
            id="invoice-items"
          >
            <InvoiceItems
              :config="fieldsConfig.invoiceItems"
              :invoice-items="orderedSelectedItems"
              :vat-rate-options="vatRateOptions"
              :vat-rate-select-hidden="vatRateSelectHidden"
              @addInvoiceItem="handleAddInvoiceItem"
              @duplicateItem="handleDuplicateInvoiceItem"
              @removeItem="handleRemoveInvoiceItem"
              @updateItem="handleUpdateInvoiceItem"
              @updateOrder="handleUpdateOrder"
            />

            <InvoiceSummary
              :config="fieldsConfig.summary"
              :invoice-items="orderedSelectedItems"
              @total-changed="handleTotalChanged"
            />

            <div class="text-center mt-40">
              <div
                class="invoice__button"
                id="invoice-button"
              >
                <Button
                  @click="createInvoice"
                  full-width
                  icon="icon icon-send"
                  new-design
                  size="extra-large"
                  type="warning"
                  with-long-text
                >
                  {{ $t('pages.profession.generator.invoiceButton') }}
                </Button>
              </div>
            </div>
          </div>
        </Form>
      </div>
    </div>

    <transition name="sticky-bar-slide">
      <div
        class="sticky-bar"
        :class="{ 'sticky-bar--highlighted': isStickyBarHighlighted }"
        v-show="showStickyBar"
      >
        <div class="block py-0">
          <div class="block__content">
            <div class="sticky-bar__button-wrapper">
              <Button
                v-show="showStartButtons"
                @click="scrollToElementById('profession-items')"
                class="sticky-bar__button"
                new-design
                size="large"
                type="warning"
                with-long-text
              >
                {{ pageData.ctaButton1Text }}
              </Button>
              <Button
                v-show="showStartButtons"
                @click="scrollToElementById('invoice-items')"
                class="sticky-bar__button"
                new-design
                size="large"
                type="primary"
                with-long-text
              >
                {{ pageData.ctaButton2Text }}
              </Button>

              <div
                v-show="showReadyButton"
                class="sticky-bar__button-wrapper"
              >
                <span class="text--sm text--light">
                  {{ $t('pages.profession.stickyBar.total', { total }) }}
                </span>
                <Button
                  @click="createInvoice"
                  class="sticky-bar__button"
                  :icon="isMobile ? '' : 'icon icon-send'"
                  new-design
                  size="large"
                  type="warning"
                  with-long-text
                >
                  {{ $t('pages.profession.stickyBar.button.issueAndSendDownloadInvoice') }}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { PROFESSION_QUERY } from '~/queries';
import { PATH_INVOICE, PATH_INVOICE_KIND } from '~/router';
import { isEmpty, omit, propOr } from 'ramda';
import { mapGetters, mapState } from 'vuex';
import { mapFields } from 'vuex-map-fields';
import { toCamelCase } from 'keys-transform';
import debounce from 'throttle-debounce/debounce';
import formConfig from '~/usa/formConfig';
import globalFormConfig from '~/components/Profession/formConfig';

export default {
  name: 'ProfessionPage',
  components: {
    EmptyContainer: () => import('~/components/Table/EmptyContainer.vue'),
    Hero: () => import('~/components/Profession/Hero.vue'),
    InvoiceItems: () => import('~/components/Profession/InvoiceItems.vue'),
    InvoiceSummary: () => import('~/components/Profession/InvoiceSummary.vue'),
    ProfessionItems: () => import('~/components/Profession/ProfessionItems.vue'),
    TypeOfDocument: () => import('~/usa/components/InvoiceForm/sections/TypeOfDocument.vue'),
    VatCalculation: () => import('~/components/Profession/VatCalculation.vue'),
  },
  data: () => ({
    debouncedSearch: '',
    isStickyBarHighlighted: false,
    nextItemId: 1,
    orderedSelectedItems: [],
    pageData: {},
    PATH_INVOICE,
    professionData: [],
    search: '',
    showReadyButton: false,
    showStartButtons: true,
    showStickyBar: false,
    showTypeOfDocument: false,
    total: 0,
    typeOfDocumentModel: { kind: 'invoice' },
  }),
  apollo: {
    profession: {
      query: PROFESSION_QUERY,
      variables() {
        return {
          slug: this.$route.params.slug,
          locale: document.documentElement.lang,
          preview: !!this.$route?.query?.preview,
          documentType: this.$route.params.document_type,
        };
      },
      result({ data }) {
        if (data?.profession?.formItems) {
          this.professionData = propOr([], 'formItems', data.profession);

          // Merge core profession data with document-specific data for backward compatibility
          this.pageData = {
            ...omit(['formItems', 'document'], data.profession),
            ...(data.profession.document || {}),
          };

          this.typeOfDocumentModel.kind = data.profession.documentType;

          this.initializeProfessionData();
        }
      },
    },
  },
  computed: {
    ...mapFields('invoiceForm', ['vat_calculation']),
    ...mapGetters('config', ['defaultUnitTypes']),
    ...mapState('invoiceForm', ['seller_address_country_code']),
    ...mapState('config', {
      kindOptions: state => state.invoice.kinds,
      global: state => state.global,
    }),
    resolvedDocumentTypes() {
      if (this.isUsaRegion) return null;

      return this.kindOptions.map(({ key, value }) => ({
        label: value,
        value: key,
      }));
    },
    backgroundImage() {
      const imageUrl = this.pageData?.formBackgroundImage;
      if (!imageUrl || isEmpty(imageUrl)) return null;

      return `${imageUrl}&w=440&h=auto`;
    },
    fieldsConfig() {
      const kind = this.invoiceKind ? toCamelCase(this.invoiceKind) : '';
      const config = this.isUsaRegion ? formConfig : globalFormConfig;
      const configFunction = config[kind] || config.default;

      return configFunction();
    },
    filteredProfessionData() {
      const searchTerm = (this.debouncedSearch || '').toLowerCase().trim();

      if (!searchTerm) {
        return this.professionData.map((category, originalIndex) => ({
          originalIndex,
          category: {
            ...category,
            subcategories: category.subcategories.map((subcategory, subOriginalIndex) => ({
              originalIndex: subOriginalIndex,
              subcategory,
            })),
          },
        }));
      }

      const categories = this.professionData.map((category, originalIndex) => ({
        originalIndex,
        category: {
          ...category,
          subcategories: category.subcategories
            .map((subcategory, subOriginalIndex) => ({
              originalIndex: subOriginalIndex,
              subcategory: {
                ...subcategory,
                items: this.filterItemsList(subcategory.items, searchTerm),
              },
            }))
            .filter(entry => entry.subcategory.items.length > 0),
        },
      }));

      return categories.filter(entry => entry.category.subcategories.length > 0);
    },
    hasNoSearchResults() {
      return this.filteredProfessionData.length === 0;
    },
    invoiceKind() {
      return this.typeOfDocumentModel.kind;
    },
    isUsaRegion() {
      return this.$i18n.locale === 'en';
    },
    loading() {
      return this.$apollo.loading;
    },
    searchInputLabel() {
      return this.pageData.searchPrompt || this.$i18n.t('pages.profession.generator.search');
    },
    sectionHeader() {
      const translationKey = this.isUsaRegion ? 'usa.form.invoiceForm.constants.documentTypes' : 'invoice_kind.options';

      return this.isUsaRegion ? this.$i18n.t(`${translationKey}.${toCamelCase(this.invoiceKind)}`) : this.$i18n.t(`${translationKey}.${this.invoiceKind}`);
    },
    countryVatRates() {
      const config = this.global.vat_schemes.find(
        obj => obj.code === this.seller_address_country_code,
      );
      return config?.current_rates || [];
    },
    vatRateOptions() {
      if (this.isUsaRegion) {
        const baseRates = [0, 4, 5, 6, 6.25, 7, 7.25, 8, 9, 10];
        const additionalRates = new Set();

        if (this.professionData && this.professionData.length > 0) {
          this.professionData.forEach((category) => {
            category.subcategories.forEach((subcategory) => {
              subcategory.items.forEach((item) => {
                if (item.vat_rate !== undefined && item.vat_rate !== null && typeof item.vat_rate === 'number') {
                  additionalRates.add(item.vat_rate);
                }
              });
            });
          });
        }

        return [...new Set([...baseRates, ...additionalRates])].sort((a, b) => a - b);
      }

      let rates = [0];

      if (this.countryVatRates.length > 0) {
        rates = rates.concat(this.countryVatRates.map(key => key.rate));
      }

      return rates;
    },
    defaultVatRate() {
      if (!this.isUsaRegion && this.countryVatRates.length > 0) {
        const standardRate = this.countryVatRates.find(r => r.kind === 'standard');
        return standardRate ? standardRate.rate : 0;
      }

      return 0;
    },
    vatRateSelectHidden() {
      return !this.fieldsConfig?.vatCalculation?.visible;
    },
  },
  methods: {
    filterItemsList(items, searchTerm) {
      if (!searchTerm) return items;

      const searchWords = searchTerm.split(' ').filter(word => word.length > 0);

      return items.filter((item) => {
        if (item && item._isNewItem) return true;

        const description = (item && item.description ? String(item.description) : '').toLowerCase();

        return searchWords.every(word => description.includes(word));
      });
    },
    visibleIndexesFor(items) {
      const searchTerm = (this.debouncedSearch || '').toLowerCase().trim();
      if (!searchTerm) {
        return items.map(() => true);
      }

      const searchWords = searchTerm.split(' ').filter(word => word.length > 0);

      return items.map((item) => {
        if (item && item._isNewItem) return true;

        const description = (item && item.description ? String(item.description) : '').toLowerCase();
        return searchWords.every(word => description.includes(word));
      });
    },
    checkElementVisibility() {
      const elements = {
        heroButtons: document.getElementById('hero-buttons'),
        firstProfessionItem: document.getElementById('first-profession-item'),
        invoiceButton: document.getElementById('invoice-button'),
      };

      if (!elements.heroButtons || !elements.firstProfessionItem || !elements.invoiceButton) return;

      const windowHeight = window.innerHeight;
      const positions = {
        heroButtons: elements.heroButtons.getBoundingClientRect(),
        firstProfessionItem: elements.firstProfessionItem.getBoundingClientRect(),
        invoiceButton: elements.invoiceButton.getBoundingClientRect(),
      };

      const isVisible = {
        heroButtons: positions.heroButtons.top < windowHeight + 100,
        firstProfessionItem: positions.firstProfessionItem.top < windowHeight,
        invoiceButtonNear: positions.invoiceButton.top < windowHeight + 100,
        invoiceButton: positions.invoiceButton.top < windowHeight && positions.invoiceButton.bottom > 0,
      };

      this.showStickyBar = false;
      this.showStartButtons = false;
      this.showReadyButton = false;
      this.isStickyBarHighlighted = false;

      if (isVisible.invoiceButton) return;

      if (isVisible.firstProfessionItem && !isVisible.invoiceButtonNear) {
        this.isStickyBarHighlighted = true;
        this.showStickyBar = true;
        this.showReadyButton = true;
      } else if (!isVisible.heroButtons) {
        this.isStickyBarHighlighted = false;
        this.showStickyBar = true;
        this.showStartButtons = true;
      }
    },
    createInvoice() {
      this.orderedSelectedItems = this.orderedSelectedItems.filter(item => item.description || item.price > 0 || (item.quantity && item.quantity > 1));

      if (this.isUsaRegion) {
        this.$router.push({
          name: PATH_INVOICE,
          params: {
            linesWithVatRate: this.orderedSelectedItems,
            kind: this.invoiceKind,
            showInvoiceFormGuide: true,
          },
        });
      } else {
        this.$router.push({
          name: PATH_INVOICE_KIND,
          params: {
            lines: this.orderedSelectedItems,
            kind: this.invoiceKind,
            hideGeneralSettings: true,
            showInvoiceFormGuide: true,
          },
          query: {
            form_version: 'v3',
          },
        });
      }
    },
    findFirstEmptyItem() {
      return this.orderedSelectedItems.findIndex(item => this.isEmptyItem(item));
    },
    findItemInProfessionData(categoryIndex, subcategoryIndex, itemIndex) {
      return this.professionData[categoryIndex]?.subcategories[subcategoryIndex]?.items[itemIndex];
    },
    handleAddInvoiceItem() {
      const lastItem = this.orderedSelectedItems.length > 0 ? this.orderedSelectedItems[this.orderedSelectedItems.length - 1] : null;

      const newItem = {
        description: '',
        price: 0,
        unit_type: lastItem?.unit_type || this.defaultUnitTypes?.[0] || 'ks',
        vat_rate: lastItem?.vat_rate || 0,
        quantity: 1,
        isSelected: true,
        newItemId: this.nextItemId,
      };
      this.orderedSelectedItems.push(newItem);
      this.nextItemId += 1;
    },
    handleDuplicateInvoiceItem({ index, item }) {
      const duplicatedItem = {
        description: item.description,
        price: item.price,
        unit_type: item.unit_type,
        vat_rate: item.vat_rate,
        quantity: item.quantity,
        isSelected: true,
        newItemId: this.nextItemId,
      };

      this.orderedSelectedItems.splice(index + 1, 0, duplicatedItem);
      this.nextItemId += 1;
    },
    handleProfessionItemsUpdate({ categoryIndex, subcategoryIndex, items }) {
      this.professionData[categoryIndex].subcategories[subcategoryIndex].items = items;

      items.forEach((item, itemIndex) => {
        if (item.isSelected) {
          const itemWithIndexes = {
            ...item,
            categoryIndex,
            subcategoryIndex,
            itemIndex,
          };

          const existingIndex = this.orderedSelectedItems.findIndex(orderedItem => this.itemsMatch(orderedItem, itemWithIndexes));

          if (existingIndex !== -1) {
            this.$set(this.orderedSelectedItems, existingIndex, itemWithIndexes);
          } else {
            const emptyItemIndex = this.findFirstEmptyItem();
            if (emptyItemIndex !== -1) {
              this.$set(this.orderedSelectedItems, emptyItemIndex, itemWithIndexes);
            } else {
              this.orderedSelectedItems.push(itemWithIndexes);
            }
          }
        } else {
          this.orderedSelectedItems = this.orderedSelectedItems.filter(orderedItem => !this.itemsMatch(orderedItem, {
            categoryIndex,
            subcategoryIndex,
            itemIndex,
          }));
        }
      });
    },
    handleRemoveInvoiceItem({ item }) {
      if (this.hasValidIndexes(item)) {
        const originalItem = this.findItemInProfessionData(
          item.categoryIndex,
          item.subcategoryIndex,
          item.itemIndex,
        );
        if (originalItem) {
          originalItem.isSelected = false;
        }
      }

      const indexToRemove = this.orderedSelectedItems.findIndex(orderedItem => this.itemsMatch(orderedItem, item));

      if (indexToRemove !== -1) {
        this.orderedSelectedItems.splice(indexToRemove, 1);
      }
    },
    handleScroll: debounce(10, function () {
      this.checkElementVisibility();
    }),
    handleSearch: debounce(200, function () {
      this.debouncedSearch = this.search;
    }),
    handleUpdateInvoiceItem({ index, item }) {
      if (index >= 0 && index < this.orderedSelectedItems.length) {
        this.$set(this.orderedSelectedItems, index, item);

        if (this.hasValidIndexes(item)) {
          const originalItem = this.findItemInProfessionData(
            item.categoryIndex,
            item.subcategoryIndex,
            item.itemIndex,
          );
          if (originalItem) {
            Object.assign(originalItem, item);
          }
        }
      }
    },
    handleUpdateOrder(newOrder) {
      this.orderedSelectedItems = newOrder;
    },
    handleTotalChanged(value) {
      this.total = value;
    },
    hasValidIndexes(item) {
      return typeof item.categoryIndex !== 'undefined'
             && typeof item.subcategoryIndex !== 'undefined'
             && typeof item.itemIndex !== 'undefined';
    },
    initializeProfessionData() {
      this.professionData.forEach((category) => {
        category.subcategories.forEach((subcategory) => {
          subcategory.items.forEach((item) => {
            item.isSelected = false;
            if (typeof item.vat_rate === 'undefined') {
              item.vat_rate = 0;
            }
          });
        });
      });
    },
    isEmptyItem(item) {
      return item.newItemId
             && (!item.description || item.description.trim() === '')
             && (item.price === 0 || item.price === undefined || item.price === null);
    },
    itemsMatch(item1, item2) {
      if (item1.newItemId && item2.newItemId) {
        return item1.newItemId === item2.newItemId;
      }

      return item1.categoryIndex === item2.categoryIndex
             && item1.subcategoryIndex === item2.subcategoryIndex
             && item1.itemIndex === item2.itemIndex;
    },
    resetSearch() {
      this.search = '';
    },
    scrollToElementById(elementId) {
      const element = document.getElementById(elementId);
      if (element) {
        this.$store.dispatch('uiEffects/scrollToElement', element);
      }
    },
  },
  watch: {
    orderedSelectedItems: {
      handler() {
        this.$nextTick(() => {
          setTimeout(() => {
            this.checkElementVisibility();
          }, 250);
        });
      },
    },
    vatRateSelectHidden(newValue) {
      if (newValue === true) {
        this.vat_calculation = 'vat_inclusive';
      }
    },
  },
  mounted() {
    if (this.orderedSelectedItems.length === 0) {
      this.handleAddInvoiceItem();
    }

    window.addEventListener('scroll', this.handleScroll);

    this.$nextTick(() => {
      this.checkElementVisibility();
    });
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
};
</script>

<style lang="scss" scoped>
.main {
  min-height: unset;
  margin-bottom: 40px;
}

.block {
  position: relative;
  padding: 40px 0;
}

.block--with-image-background {
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: var(--bg-image);
    background-image: image-set(var(--bg-image) 1x, var(--bg-image-2x) 2x);
    background-repeat: repeat;
    background-size: 440px 440px;
    opacity: .16;
    pointer-events: none;
  }
}

.block--first {
  padding-top: 48px;

  @media only screen and (max-width: $--sm - 1px) {
    padding-top: 32px;
    padding-bottom: 0;
  }
}

.block--last {
  padding-bottom: 80px;

  @media only screen and (max-width: $--sm - 1px) {
    padding-bottom: 40px;
  }
}

.block--with-light-background {
  background: $--color-white;
}

.block__content--wide {
  max-width: 1400px;
}

.profession,
.invoice {
  background: $--color-white;
  border-radius: 12px;
  border: 1px solid $--border-color-base;
  margin: 0 auto;
  max-width: 1110px;
  position: relative;

  &__button {
    display: inline-block;

    @media only screen and (max-width: $--sm - 1px) {
      width: 100%;
    }
  }
}

.invoice {
  margin-top: 80px;
}

.profession__heading {
  line-height: 160%;

  @media only screen and (max-width: $--sm - 1px) {
    font-size: rem(22px);
    font-weight: 600;
  }
}

.profession__type-wrapper {
  margin-top: 32px;

  :deep() {
    .section-header {
      margin: 0 0 32px;

      .heading-with-image {
        @media only screen and (max-width: $--sm - 1px) {
          margin-top: 0;
        }
      }
    }

    .section-header__control {
      align-self: center;
    }

    .heading-with-image {
      font-size: rem(18px);
      line-height: 160%;
    }

    .heading-description {
      font-weight: 600;
      line-height: 1.2;
    }
  }
}

.profession__type-link {
  text-decoration: none;
}

.profession__search-wrapper {
  display: flex;
  gap: 24px;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;

  @media only screen and (max-width: $--md - 1px) {
    flex-wrap: wrap;
  }
}

.profession__category {
  margin-bottom: 32px;
}

.profession__subcategory {
  margin-top: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid $--border-color-base;

  @media only screen and (max-width: $--sm - 1px) {
    margin-top: 36px;
    padding-bottom: 36px;

    &:first-of-type {
      margin-top: 24px;
    }
  }
}

.search {
  flex: 0 1 60%;
  margin-bottom: 0;

  @media only screen and (max-width: $--md - 1px) {
    flex: 1 0 100%;
  }
}

.delimiter {
  border-top-color: $--border-color-base;
}

.sticky-bar {
  position: sticky;
  bottom: 0;
  border-top: 1px solid $--border-color-base;
  padding: 8px;
  background-color: $--color-background;
  z-index: $--index-top;
}

.sticky-bar--highlighted {
  background-color: $--color-primary;
}

.sticky-bar__button-wrapper {
  display: flex;
  gap: 12px;
  justify-content: center;
  align-items: center;

  @media only screen and (max-width: $--sm - 1px) {
    gap: 8px;
    flex-wrap: wrap;
  }
}

.sticky-bar__button {
  @media only screen and (max-width: $--sm - 1px) {
    width: 100%;
    margin: 0;
    flex-shrink: 0;
  }
}

// Animace pro sticky bar - slide up/down

.sticky-bar-slide-enter-active,
.sticky-bar-slide-leave-active {
  transition: all .25s ease-in-out;
  transform: translateY(0);
}

.sticky-bar-slide-enter,
.sticky-bar-slide-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

// Animace pro TypeOfDocument - slide down/fade

.type-of-document-slide-enter-active,
.type-of-document-slide-leave-active {
  transition: all .25s ease-in-out;
  transform-origin: top;
}

.type-of-document-slide-enter,
.type-of-document-slide-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}
</style>
